import * as React from 'react'

import apis from '@app/api/api'
import useI18n from '@store/i18n/useI18n'
import Logger from '@utils/Logger'
import theme from '@theme/Theme'
import { motion, container } from '@utils/Motion'

import styled from '@theme/styled-components'
import Searchbar from '@components/searchbar/searchbar'
import Loader from 'react-loader-spinner'

import * as JsSearch from 'js-search'
import AccentSanitizer from '../../../utils/AccentSanitizer'
import Swal from 'sweetalert2'
import ScreenContainer from '@components/screenContainer/screenContainer'
import InfiniteList from '@components/infiniteList/infiniteList'
import AdminCompanies from '@components/listItem/adminCompanies'
import BusinessesMultiSelect from '@components/select/businessesMultiSelect'
import { buildParams } from '@utils/buildParamsWS'
import AdminTextValue from '@components/text/textArea'

interface Props {
  navigation: Navigation
}

interface UsersFilters {
  firstname?: string
  lastname?: string
  email?: string
}

const TABLE_ORDER = (i18n: I18n) => [
  `${i18n.t('components.adminCompanies.table.tableHeader.role')}`,
  `${i18n.t('components.adminCompanies.table.tableHeader.lastname')}`,
  `${i18n.t('components.adminCompanies.table.tableHeader.firstname')}`,
  `${i18n.t('components.adminCompanies.table.tableHeader.email')}`,
]

const sanitizer = new AccentSanitizer()
const searchjs = new JsSearch.Search('id')

searchjs.searchIndex = new JsSearch.UnorderedSearchIndex()
searchjs.indexStrategy = new JsSearch.AllSubstringsIndexStrategy()
searchjs.tokenizer = {
  tokenize: (text: string) => text.split(' '),
}
searchjs.sanitizer = sanitizer
searchjs.addIndex('libelle')
searchjs.addIndex('idPresta')

const adminCompanies = ({ navigation }: Props) => {
  const i18n = useI18n()
  const [screenStatus, setScreenStatus] = React.useState<'fetched' | 'loading' | 'error'>('loading')

  const [text, setText] = React.useState<string>('')
  const [filters, setFilters] = React.useState<UsersFilters>()
  const [adminCompanies, setAdminCompanies] = React.useState<AdminCompany[]>([])

  const [selectedCompany, setSelectedCompany] = React.useState<AdminCompany | undefined>(undefined)
  const [selectedCompanyBusinesses, setSelectedCompanyBusinesses] =
    React.useState<AdminCompanyBusiness[] | undefined>(undefined)
  const [initialUsersLength, setInitialUsersLength] = React.useState<number>()
  const [listUsers, setListUsers] = React.useState<AdminCompanyUser[] | undefined>(undefined)
  const [regionList, setRegionList] = React.useState<Option[]>([])
  const [dirtyForm, setDirtyForm] = React.useState<boolean>(false)

  React.useEffect(() => {
    fetchCompanies()
  }, [])

  React.useEffect(() => {
    if (selectedCompany) {
      setInitialUsersLength(undefined)
      Promise.all([
        apis.admin.company.listBusinesses(selectedCompany.id),
        apis.admin.company.getUsers(selectedCompany.id),
      ]).then(([listBusinesses, listUsers]) => {
        setSelectedCompanyBusinesses(listBusinesses.businesses)
        setInitialUsersLength(listUsers.users.length)
        setListUsers(listUsers.users)
      })
    }
  }, [selectedCompany])

  const businessBoardTable: string[][] = React.useMemo(() => {
    const tableToDisplay = TABLE_ORDER(i18n).map((header) => [header])
    if (!!listUsers) {
      listUsers.map((user) => {
        tableToDisplay[0].push(i18n.t(`components.adminCompanies.table.role.${user.role}`))
        tableToDisplay[1].push(user.lastname)
        tableToDisplay[2].push(user.firstname)
        tableToDisplay[3].push(user.email)
      })
    }
    return tableToDisplay
  }, [listUsers])

  const fetchUsers = (filters: UsersFilters) => {
    if (selectedCompany) {
      apis.admin.company
        .getUsers(
          selectedCompany.id,
          buildParams([
            { nameParam: 'firstname', value: filters.firstname },
            { nameParam: 'lastname', value: filters.lastname },
            { nameParam: 'email', value: filters.email },
          ])
        )
        .then((res) => setListUsers(res.users))
    }
  }

  const fetchCompanies = () =>
    Promise.all([apis.admin.list.companies(), apis.admin.list.regions()])
      .then(([fetchedCompanies, fetchedRegions]) => {
        setAdminCompanies(fetchedCompanies.companies)
        setRegionList(
          fetchedRegions.regions.map((r) => {
            return { value: r.id, label: r.libelle }
          })
        )
        searchjs.addDocuments(fetchedCompanies.companies)
        setScreenStatus('fetched')
      })
      .catch((err) => {
        Logger.info('ERROR FETCHING ADMIN COMPANIES : ', err)
        setScreenStatus('error')
      })

  const setFormField = (callback: () => void) => {
    setDirtyForm(true)
    callback()
  }

  const alertCancellingEdition = (callback?: () => void) => {
    if (dirtyForm) {
      Swal.fire({
        title: i18n.t('components.adminCompanies.alertCancelling.title'),
        text: i18n.t('components.adminCompanies.alertCancelling.text'),
        showCancelButton: true,
        confirmButtonText: i18n.t('components.adminCompanies.alertCancelling.confirm'),
        confirmButtonColor: 'green',
        cancelButtonText: i18n.t('components.adminCompanies.alertCancelling.cancel'),
        cancelButtonColor: 'red',
      }).then((result) => {
        if (result.isConfirmed) {
          setSelectedCompany(undefined)
          setDirtyForm(false)
          !!callback && callback()
        }
      })
    } else {
      setSelectedCompany(undefined)
      setDirtyForm(false)
      !!callback && callback()
    }
  }

  const searchedCompanies: AdminCompany[] = React.useMemo(
    () => (text && searchjs ? searchjs.search(sanitizer.sanitize(text.trim())) : adminCompanies) as AdminCompany[],
    [adminCompanies, text]
  )

  const sortedCompanies: AdminCompany[] = React.useMemo(
    () => searchedCompanies?.sort((a, b) => a.libelle.localeCompare(b.libelle)),
    [searchedCompanies]
  )

  const selectedRegion: Option | undefined = React.useMemo(
    () => regionList.find((r) => selectedCompany && r.value === selectedCompany.regionId) || undefined,
    [selectedCompany]
  )

  const renderUserTab = () => (
    <>
      {initialUsersLength && initialUsersLength > 0 ? (
        <BoardContainer>
          {businessBoardTable.map((column, columnIdx) => (
            <TabColumn key={columnIdx}>
              {column.map((row, rowIdx) => (
                <TabRow key={`${columnIdx} - ${rowIdx}`} header={rowIdx === 0} odd={rowIdx % 2 === 0}>
                  {displayValue(row, rowIdx, columnIdx)}
                </TabRow>
              ))}
            </TabColumn>
          ))}
        </BoardContainer>
      ) : initialUsersLength === 0 ? (
        <NoUserContainer>{i18n.t('components.adminCompanies.table.noValue')}</NoUserContainer>
      ) : (
        <LoaderContainer>
          <Loader type="TailSpin" color={theme.colors.classicButtonLoader} height={35} />
        </LoaderContainer>
      )}
    </>
  )
  const displayValue = (row: string, rowIdx: number, columnIdx: number) => {
    return (
      <>
        <TabValue header={rowIdx === 0} odd={rowIdx % 2 === 0}>
          {row}
          {rowIdx === 0 && columnIdx !== 0 && (
            <InputText
              value={columnIdx === 1 ? filters?.lastname : columnIdx === 2 ? filters?.firstname : filters?.email}
              onChange={(evt) =>
                setFilters({
                  lastname: columnIdx === 1 ? evt.target.value : filters?.lastname,
                  firstname: columnIdx === 2 ? evt.target.value : filters?.firstname,
                  email: columnIdx === 3 ? evt.target.value : filters?.email,
                })
              }
              placeholder={i18n.t('components.adminCompanies.table.search')}
              onKeyPress={() => {
                if (filters) {
                  const timer = setTimeout(() => fetchUsers(filters), 300)
                  return () => clearTimeout(timer)
                }
              }}
              disabled={false}
            />
          )}
        </TabValue>
      </>
    )
  }

  return (
    <ScreenContainer
      menu={
        <LeftContainer>
          <ScreenTitleContainer>
            <ScreenTitle>{i18n.t('components.adminCompanies.screenTitle')}</ScreenTitle>
            <Back onClick={() => navigation.push('/')}>{i18n.t('components.menu.back')}</Back>
          </ScreenTitleContainer>
          {screenStatus === 'loading' ? (
            <StatusContainer>
              <Loader type="ThreeDots" color={theme.colors.classicButtonLoader} height={30} />
            </StatusContainer>
          ) : screenStatus === 'error' ? (
            <ErrorLibelle>{i18n.t('components.adminCompanies.error')}</ErrorLibelle>
          ) : (
            <>
              <Legend>
                <LeftButton>{i18n.t('components.adminCompanies.level.companies')}</LeftButton>
                <RigthButton
                  onClick={() => {
                    navigation.navigate('/admin/companies/group')
                  }}>
                  {i18n.t('components.adminCompanies.level.group')}
                </RigthButton>
              </Legend>
              <Searchbar placeholder={i18n.t('components.adminCompanies.searchbar')} text={text} setText={setText} />
              <CompanyList
                as={motion.div}
                className="container"
                variants={container}
                initial="hidden"
                animate="visible">
                {sortedCompanies.length === 0 ? (
                  <NoItemContainer>
                    <NoItemLibelle>{i18n.t('components.adminCompanies.noItem')}</NoItemLibelle>
                  </NoItemContainer>
                ) : (
                  <InfiniteList
                    data={sortedCompanies}
                    height={100}
                    renderItem={(c) => (
                      <AdminCompanies
                        company={c}
                        height={100}
                        onClick={() =>
                          alertCancellingEdition(() => {
                            apis.admin.company
                              .get(c.id)
                              .then((loaded) => {
                                setSelectedCompany(loaded)
                              })
                              .catch((err) => {
                                Logger.error('Unable to load company', err)
                              })

                            apis.admin.company.get
                          })
                        }
                      />
                    )}
                  />
                )}
              </CompanyList>
            </>
          )}
        </LeftContainer>
      }
      content={
        <RightContainer>
          {!!selectedCompany ? (
            <FormContainer>
              <SectionTitleContainer>{i18n.t('components.adminCompanies.section01')}</SectionTitleContainer>
              <ColumnContainer>
                <RowContainer>
                  <FormItemContainer>
                    <AdminTextValue
                      title={i18n.t('components.adminCompanies.item01')}
                      value={selectedCompany.libelle}
                      placeholder={i18n.t('common.placeholder.name')}
                    />
                  </FormItemContainer>
                  <FormItemContainer>
                    <AdminTextValue
                      title={i18n.t('components.adminCompanies.item02')}
                      value={selectedRegion?.label}
                      placeholder={i18n.t('common.placeholder.name')}
                    />
                  </FormItemContainer>
                </RowContainer>
              </ColumnContainer>
              <SectionTitleContainer>{i18n.t('components.adminCompanies.section06')}</SectionTitleContainer>
              <ColumnContainer>
                <RowContainer>
                  <FormItemContainer>
                    <SelectorContainer>
                      <SelectContainer>
                        <AllSelectedContainer>
                          {!!selectedCompany.supplierCodes && selectedCompany.supplierCodes.length > 0 ? (
                            <>
                              {selectedCompany.supplierCodes?.map((code) => (
                                <SelectedContainer key={code}>
                                  <SelectedLabel>{code}</SelectedLabel>
                                </SelectedContainer>
                              ))}
                            </>
                          ) : (
                            <>{i18n.t('common.placeholder.supplierCodes')}</>
                          )}
                        </AllSelectedContainer>
                      </SelectContainer>
                    </SelectorContainer>
                  </FormItemContainer>
                </RowContainer>
              </ColumnContainer>
              <SectionTitleContainer>{i18n.t('components.adminCompanies.section04')}</SectionTitleContainer>
              <ColumnContainer>
                <RowContainer>
                  <FormItemContainer>
                    <SelectorContainer>
                      <SelectorTitle>{i18n.t('components.adminCompanies.item04')}</SelectorTitle>
                      <BusinessesMultiSelect
                        regionList={regionList}
                        selected={selectedCompanyBusinesses?.map((b) => ({ value: b?.id, label: b?.idGrdf })) ?? []}
                        setSelected={(selected) =>
                          setFormField(() =>
                            setSelectedCompanyBusinesses(selected.map((s) => ({ id: s.value, idGrdf: s.label })) || [])
                          )
                        }
                        placeholder={i18n.t('components.detailsOrphan.placeholder.business')}
                      />
                    </SelectorContainer>
                  </FormItemContainer>
                </RowContainer>
              </ColumnContainer>
              <SectionTitleContainer>{i18n.t('components.adminCompanies.section05')}</SectionTitleContainer>
              <ColumnContainer>{renderUserTab()}</ColumnContainer>
            </FormContainer>
          ) : (
            <NoSelectedContainer>
              <NoSelectedLibelle>{i18n.t('components.adminCompanies.noSelected')}</NoSelectedLibelle>
            </NoSelectedContainer>
          )}
        </RightContainer>
      }
    />
  )
}

export default adminCompanies

const LeftContainer = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px 10px 0 10px;
  overflow: hidden;
`
const StatusContainer = styled('div')`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 100%;
`
const ErrorLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
  margin: 40px;
`
const Back = styled('div')`
  ${(props) => props.theme.fonts.backLibelle};
  cursor: pointer;
`
const ScreenTitle = styled('div')`
  ${(props) => props.theme.fonts.title};
`
const ScreenTitleContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 10px;
  margin: 0 10px 15px 10px;
`
const CompanyList = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: scroll;
`
const NoItemContainer = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`
const NoItemLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
`
const RightContainer = styled('div')`
  flex: 1;
  display: flex;
  background-color: ${(props) => props.theme.colors.filterBackgroundColor};
  overflow: scroll;
`
const FormContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin: 50px;
  justify-content: flex-start;
`
const NoSelectedContainer = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`
const NoUserContainer = styled('div')`
  margin-bottom: 50px;
`
const NoSelectedLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
`
const SectionTitleContainer = styled('div')`
  font-family: Avenir;
  font-style: normal;
  font-weight: 900;
  font-size: 20px;
  line-height: 24px;
  color: #0053a1;
  margin: 10px;
  margin-top: 0;
`
const RowContainer = styled('div')`
  display: flex;
  margin: 10px;
  margin-top: 0;
`
const ColumnContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin: 10px;
  margin-top: 0;
`
const FormItemContainer = styled('div')`
  display: flex;
  flex: 1;
  margin: 10px;
  margin-top: 0;
`
const SelectorContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;

  margin: 5px;
`
const SelectorTitle = styled('div')`
  font-family: Avenir;
  font-style: normal;
  font-weight: 900;
  font-size: 14px;
  line-height: 19px;
  color: #9494a2;
`

const Legend = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  margin-bottom: 20px;
  border-radius: 7px;
  background-color: ${(props) => props.theme.colors.greyBackground};
`
const LeftButton = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border-radius: 7px;
  color: ${(props) => props.theme.colors.secondary_text};
  background-color: ${(props) => props.theme.colors.primary_green};
`
const RigthButton = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border-radius: 7px;
  color: ${(props) => props.theme.colors.secondary_grey};
  background-color: ${(props) => props.theme.colors.greyBackground};
`

const BoardContainer = styled('div')`
  display: flex;
  flex-direction: row;

  margin: 25px;
  margin-bottom: 100px;

  border-radius: 10px;
  height: fit-content;

  box-shadow: 0px 0px 5px 1px #2224;
  overflow: scroll;
  height: fit-content;
  max-height: 200px;
`

const TabColumn = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: white;
  height: fit-content;
`
const TabRow = styled('div')<{ header: boolean; odd: boolean }>`
  border: 0px solid ${(props) => props.theme.colors.headerBackgroundColor};
  border-right-width: 1px;
  border-bottom-width: 1px;

  display: flex;
  height: fit-content;

  align-items: center;
  justify-content: center;
  flex-direction: column;

  height: 50px;
  max-height: 50px;
  min-height: 50px;

  background-color: ${(props) =>
    props.header
      ? props.theme.colors.headerBackgroundColor
      : props.odd
      ? props.theme.colors.oddBackgroundColor
      : props.theme.colors.tabBackgroundColor};
`

const TabValue = styled('div')<{ header?: boolean; odd?: boolean }>`
  ${(props) => (props.header ? props.theme.fonts.sectionTitle : props.theme.fonts.label)};
  ${(props) => (props.header ? `font-size: 15px; line-height: 15px;` : `font-size: 13px; line-height: 13px;`)};

  color: ${(props) =>
    props.header
      ? props.theme.colors.headerColor
      : props.odd
      ? props.theme.colors.oddColor
      : props.theme.colors.tabColor};
  overflow: hidden;
  display: flex;
  justify-content: space-between;
  padding-left: 4px;
  padding-right: 4px;
  align-items: center;
  width: 90%;
`
const InputText = styled('input')`
  outline: none;
  border: none;
  padding: 5px;
  margin-right: 5px;
  background: transparent;
  border-bottom: 1px solid;
  margin-bottom: 2px;
  margin-left: 2px;
  width: 100px;
  ::placeholder {
    color: white;
    font-size: 12px;
  }
  color: white;
  font-size: 12px;
`
const LoaderContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`
const SelectContainer = styled('div')`
  border: 1px solid ${(props) => props.theme.colors.searchbarBorder};
  background: ${(props) => props.theme.colors.menuBackground};
  ${(props) => props.theme.fonts.selectLibelle}
  box-sizing: border-box;
  align-items: center;
  padding: 0 10px;
  border-radius: 7px;
  min-height: 50px;
  display: flex;
  justify-content: space-between;
`
const SelectedContainer = styled('div')`
  margin: 2px 5px 2px 0;
  display: flex;
  border-radius: 25px;
  align-items: center;
  justify-content: space-between;
  border: 1px solid ${(props) => props.theme.colors.primary_blue};
  background-color: ${(props) => props.theme.colors.filterBackgroundColor};
`
const SelectedLabel = styled('div')`
  display: flex;
  margin: 0 10px;
  align-items: center;
  justify-content: center;
`
const AllSelectedContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
`
